// AI-CONTEXT: See docs/features/participation-ladder.md
import { useContext, useEffect, useRef, useState } from 'react'
import { db } from '../../firebase/config'
import firebase from '../../firebase/compat'
import { client } from '../../hooks/Client'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import { Auth } from '../../StateManagment/Auth'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import timestampToUI from '../../utils/timestampToUI'
import calculateParticipationImpact from '../../helpers/participationHelpers/calculateParticipationImpact'
import useParticipationParameters from '../../hooks/useParticipationParameters'
import formatEuro from '../../helpers/formatEuro'
import ParticipationProfileForm from '../../components/personas/participation/ParticipationProfileForm'
import ParticipationResult from '../../components/personas/participation/ParticipationResult'
import { LadderViz, StepChangePill, LadderEndResult, ladderLabel } from '../../components/personas/participation/LadderProgress'
import ParticipationLockedSummary from '../../components/personas/participation/ParticipationLockedSummary'
import usePersonaTerms from '../../hooks/usePersonaTerms'
import recordParticipationSnapshot from '../../helpers/participationHelpers/recordParticipationSnapshot'
import moveParticipationSnapshot, { dagVan } from '../../helpers/participationHelpers/moveParticipationSnapshot'
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis'
import buildParticipationPostsView from '../../helpers/participationHelpers/buildParticipationPostsView'
import birthDatesByPersona from '../../helpers/participationHelpers/birthDatesByPersona'
import { PersonaNavigation } from '../../StateManagment/PersonaNavigation'
import resolveInterventionDose from '../../helpers/msiMonetisationHelpers/resolveInterventionDose'

const formatEuroSigned = (value) => {
    if (!value) return '€ 0,00'
    const sign = value > 0 ? '+ ' : '− '
    return `${sign}${formatEuro(Math.abs(value))}`
}

// Rekenregels MSI: waarom er niets toegerekend kon worden, met wat de gebruiker eraan kan doen.
const DOSE_REASON_TEXT = {
    'duur onbekend': 'Niet doorgerekend: duur onbekend. Vul bij Gegevens onder Traject de startdatum en de uren per week in.',
    'uren onbekend': 'Niet doorgerekend: uren onbekend. Vul bij Gegevens onder Traject de uren per week in.',
    'einde vóór start': 'Niet doorgerekend: de einddatum ligt vóór de startdatum. Controleer bij Gegevens onder Traject beide datums.',
}

const formatYears = (years) => `Looptijd ${String(years).replace('.', ',')} jaar`

const signClass = (value) => (value > 0 ? 'pos' : value < 0 ? 'neg' : 'neutral')

// Meldingen van het 2026.3-rekenmodel (calculateParticipationPosts).
const NOTICE_TEXT = {
    geboortedatumOntbreekt: 'Er staat geen geboortedatum bij Gegevens. Zonder geboortedatum wordt de leeftijdsregel (18 jaar, AOW 67 jaar) niet toegepast.',
    geboortedatumOnleesbaar: 'De geboortedatum bij Gegevens is niet te lezen. Gebruik de vorm dd-mm-jjjj. Tot die tijd wordt de leeftijdsregel niet toegepast.',
    leeftijdsovergang: 'De deelnemer passeerde tussen de metingen een leeftijdsgrens. De beginsituatie rekent daarom met de uitkering van de huidige situatie.',
}

const LockSvg = () => (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3.2" y="7" width="9.6" height="6.5" rx="1.4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M5.2 7V5.2a2.8 2.8 0 1 1 5.6 0V7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
)

// Persona sub-tab: records the participation characteristics for two moments
// (beginsituatie / huidige situatie) and shows the calculated profile + verschil.
/**
 * @param {object} props
 * @param {string} props.personaNavigation - Actieve persona-subtab; dit tabblad rendert (verborgen) tenzij 'participatieladder'
 * @param {object} props.item - Het persona-document (met `ID`, `docid` en `Participation.t0/t1`)
 */
const Participatieladder = ({ personaNavigation, item }) => {
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)
    const [, setPersonaNavigation] = useContext(PersonaNavigation)
    const { parameters, loading, error } = useParticipationParameters()
    const personaTerms = usePersonaTerms()
    const measurements = useFirestoreGeneral('ParticipationMeasurements', 'PersonaID', item.ID)
    // Geboortedatum uit de tab Gegevens, voor de leeftijdsovergang (2026.3, D5).
    // null zolang de read loopt — dat is niet hetzelfde als 'geen geboortedatum'.
    const personaData = useFirestoreGeneral('PersonaData', 'PersonaID', item.ID)
    const birthDates = birthDatesByPersona(personaData)
    const birthDate = birthDates ? birthDates[item.ID] : undefined
    // Standaard duur en uren van de organisatie (rekenregels MSI); '' zolang de read loopt.
    const compagnyMeta = useFirestoreGeneral('CompagnyMeta', 'CompagnyID', client)
    const doseLoaded = Array.isArray(compagnyMeta)

    // Meetdatum van de handmatige vastlegging — standaard vandaag, maar terug
    // te zetten zodat een later geregistreerde meting in het juiste boekjaar valt.
    const [measurementDate, setMeasurementDate] = useState(dagVan(new Date()) || '')

    const participation = item.Participation || {}
    const profileT0 = participation.t0 || {}
    const profileT1 = participation.t1 || {}
    const beginLocked = Boolean(participation.beginLocked)
    const currentUpdatedAt = profileT1.updatedAt

    // Leeftijdsovergang bij de huidige waarde: de bewerkdatum van elk
    // meetmoment (besluit P2). Een vastgelegde meting rekent met haar eigen datum.
    const contextFor = (t1Date) => ({ birthDate, t0Date: profileT0.updatedAt, t1Date })

    // Verplichte meting (methodiek-besluit D4): elke t1-profielwijziging schrijft
    // automatisch een dag-snapshot naar ParticipationMeasurements. De ref slaat
    // de laatst geziene save over persona-wissels en de eerste render heen, zodat
    // alleen echte wijzigingen loggen — niet het openen van de pagina.
    const updatedAtMs = currentUpdatedAt && typeof currentUpdatedAt.toMillis === 'function'
        ? currentUpdatedAt.toMillis()
        : null
    const lastSeenSaveRef = useRef(/** @type {string | null} */ (null))

    useEffect(() => {
        const key = `${item.ID}|${updatedAtMs ?? 'none'}`
        const previous = lastSeenSaveRef.current

        // Eerste render of persona-wissel: alleen de baseline zetten, niet loggen.
        if (previous === null || previous.split('|')[0] !== item.ID) {
            lastSeenSaveRef.current = key
            return
        }
        if (previous === key) return
        // Pending serverTimestamp-echo: consumeren, de opgeloste waarde volgt zo.
        if (updatedAtMs === null) {
            lastSeenSaveRef.current = key
            return
        }
        // Parameters (of de geboortedatum) nog niet geladen: NIET consumeren —
        // de meting is verplicht (D4), dus het effect moet opnieuw draaien zodra
        // ze er zijn.
        if (!parameters || !birthDates) return

        lastSeenSaveRef.current = key

        const currentImpact = calculateParticipationImpact({
            participantProfileT0: participation.t0 || {},
            participantProfileT1: participation.t1 || {},
            parameters,
            context: contextFor(currentUpdatedAt),
        })

        recordParticipationSnapshot({
            client,
            personaId: item.ID,
            profile: participation.t1 || {},
            impact: currentImpact,
            parametersVersion: parameters.meta?.version,
            source: 'auto',
        }).catch((err) => console.error('participatiemeting niet vastgelegd:', err))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.ID, updatedAtMs, parameters, birthDates === null])

    const wrapStyle = { display: personaNavigation === 'participatieladder' ? 'block' : 'none' }

    // Fail visibly: without the model parameters we cannot compute anything, so
    // show the state instead of rendering misleading zeros.
    if (loading || !birthDates) {
        return (
            <div style={wrapStyle}>
                <div className="participation-ladder-container participation-ladder-status">
                    Rekenparameters laden…
                </div>
            </div>
        )
    }

    if (error || !parameters) {
        return (
            <div style={wrapStyle}>
                <div className="participation-ladder-container participation-ladder-error">
                    De rekenparameters konden niet worden geladen. Vernieuw de pagina of probeer het later opnieuw.
                </div>
            </div>
        )
    }

    // Tijdshorizon van de rekenregels: het Traject van de deelnemer, eind = de
    // laatste meting (de bewerkdatum van de huidige situatie) zonder einddatum.
    // AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md
    const dose = doseLoaded
        ? resolveInterventionDose({ persona: item, lastMeasurementMs: toMillis(currentUpdatedAt), orgDefault: compagnyMeta[0]?.DefaultInterventionDose || null })
        : null

    const impact = calculateParticipationImpact({
        participantProfileT0: profileT0,
        participantProfileT1: profileT1,
        parameters,
        context: contextFor(currentUpdatedAt),
        horizon: dose ? dose.horizon : null,
    })
    const isPosts = impact.model === 'posts'
    const postsView = isPosts ? buildParticipationPostsView(impact, parameters.calculation?.stakeholders) : null

    // Besluit D3: elke vastgelegde meting rekent met de vastgezette versie,
    // dus het bedrag in de lijst wordt hier herrekend in plaats van het bij de
    // meting opgeslagen getal te tonen (dat kan van een oudere set zijn).
    const measurementValue = (m) => calculateParticipationImpact({
        participantProfileT0: profileT0,
        participantProfileT1: m.Profile || {},
        parameters,
        context: contextFor(m.Timestamp),
    }).delta.totalSocietalValue

    const setBeginLocked = (locked) => {
        db.collection('Personas')
            .doc(item.docid)
            .update({
                'Participation.beginLocked': locked,
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            })
            .then(() => setSaved('flex'))
    }

    // Datumcorrectie van een vastgelegde meting — de datum bepaalt in welk
    // boekjaar de verandering meetelt (cohortprincipe), dus een te laat
    // geregistreerde meting moet naar de echte meetdag kunnen. Op blur, niet
    // op change: tijdens het typen van een jaartal geeft een datumveld geldige
    // tussenwaarden af (0002, 0020, …) die anders elk een echte verplaatsing
    // zouden schrijven.
    const measurementDateHandler = (measurement) => (e) => {
        const input = e.target
        if (!input.value) return

        const [y, m, d] = input.value.split('-').map(Number)

        // Zelfde plausibiliteitsgrens als de kostenjaren in de instellingen.
        if (!Number.isInteger(y) || y < 2000 || y > 2100) {
            input.value = dagVan(measurement.Timestamp) || ''
            return
        }

        moveParticipationSnapshot({
            measurement,
            newDate: new Date(y, m - 1, d),
            measurements: Array.isArray(measurements) ? measurements : [],
            deletedBy: auth?.ID || '',
        }).then((result) => {
            if (result.ok) {
                setSaved('flex')
            } else {
                alert(result.reason)
                input.value = dagVan(measurement.Timestamp) || ''
            }
        }).catch((err) => alert(`Datum aanpassen mislukt: ${err.message}`))
    }

    const sortedMeasurements = (Array.isArray(measurements) ? measurements : [])
        .slice()
        .sort((a, b) => (toMillis(b.Timestamp) ?? 0) - (toMillis(a.Timestamp) ?? 0))

    // Handmatige vastlegging — zelfde dag-upsert als de automatische snapshots
    // (methodiek-besluit D4), alleen met Source 'manual' en op de gekozen
    // meetdatum (de datum bepaalt in welk boekjaar de verandering meetelt).
    const recordMeasurement = () => {
        if (!measurementDate) return
        const [y, m, d] = measurementDate.split('-').map(Number)
        recordParticipationSnapshot({
            client,
            personaId: item.ID,
            profile: profileT1,
            impact,
            parametersVersion: parameters.meta.version,
            source: 'manual',
            date: new Date(y, m - 1, d),
        }).then(() => setSaved('flex'))
    }

    const secondaryTotals = [
        { label: 'Overheidssaldo', value: impact.delta.governmentBalance },
        { label: `Inkomensverandering ${personaTerms.singularLower}`, value: impact.delta.personalIncomeChange },
    ]

    const componentRows = isPosts ? [] : [
        { label: 'Productiviteit', value: impact.delta.incomePerson },
        { label: 'Inkomstenbelasting', value: impact.delta.incomeTax },
        { label: 'Uitkeringskosten', value: impact.delta.benefitCost },
        { label: 'Uitvoeringskosten bijstand', value: impact.delta.publicCost },
        { label: 'Waarde vrijwilligerswerk', value: impact.delta.volunteerValue },
    ]

    return (
        <div style={wrapStyle}>
            <div className="participation-ladder-container">
                {/* Verschil */}
                <div className="participation-delta">
                    <div className="participation-delta-header">
                        <div>
                            <h3>Verschil</h3>
                            <span className="subtitle">Beginsituatie → Huidige situatie</span>
                        </div>
                        <StepChangePill change={impact.delta.ladderStepChange} />
                    </div>

                    <LadderViz from={impact.t0.ladderStep} to={impact.t1.ladderStep} />

                    <LadderEndResult from={impact.t0.ladderStep} to={impact.t1.ladderStep} />

                    <div className="participation-delta-hero">
                        <span className="hk">Maatschappelijke waarde per jaar (bruto)</span>
                        <span className="hv">{formatEuroSigned(impact.delta.totalSocietalValue)}</span>
                    </div>

                    {dose && dose.horizon !== null && (
                        <div className="participation-delta-row participation-delta-strong">
                            <span className="lbl">
                                Toegerekend aan de interventie
                                <span className="participation-delta-post-type">{formatYears(dose.horizon)}</span>
                            </span>
                            <span className={`val ${signClass(impact.delta.attributedSocietalValue)}`}>
                                {formatEuroSigned(impact.delta.attributedSocietalValue)}
                            </span>
                        </div>
                    )}
                    {dose && dose.horizon === null && (
                        <p className="participation-delta-notice">
                            {DOSE_REASON_TEXT[dose.reason] || `Niet doorgerekend: ${dose.reason}.`}
                            <button type="button" className="participation-delta-notice-link" onClick={() => setPersonaNavigation('data')}>
                                Naar Gegevens
                            </button>
                        </p>
                    )}

                    {secondaryTotals.map((row) => (
                        <div key={row.label} className="participation-delta-row participation-delta-strong">
                            <span className="lbl">{row.label}</span>
                            <span className={`val ${signClass(row.value)}`}>{formatEuroSigned(row.value)}</span>
                        </div>
                    ))}

                    {isPosts ? (
                        <>
                            <p className="participation-delta-subheading">Per soort waarde</p>
                            {postsView.perType.map((row) => (
                                <div key={row.key} className="participation-delta-row">
                                    <span className="lbl">{row.label}</span>
                                    <span className={`val ${signClass(row.value)}`}>{formatEuroSigned(row.value)}</span>
                                </div>
                            ))}

                            <p className="participation-delta-subheading">Per stakeholder</p>
                            {postsView.stakeholders.length === 0 && (
                                <p className="participation-delta-empty">Geen verschil tussen beginsituatie en huidige situatie.</p>
                            )}
                            {postsView.stakeholders.map((group) => (
                                <div key={group.stakeholder} className="participation-delta-stakeholder">
                                    <div className="participation-delta-row participation-delta-strong">
                                        <span className="lbl">{group.stakeholder}</span>
                                        <span className={`val ${signClass(group.total)}`}>{formatEuroSigned(group.total)}</span>
                                    </div>
                                    {group.posts.map((row) => (
                                        <div key={`${group.stakeholder}|${row.post}`} className="participation-delta-row participation-delta-post">
                                            <span className="lbl">
                                                {row.post}
                                                <span className="participation-delta-post-type">
                                                    {row.typeWaardering}{row.overdracht ? ' · overdracht' : ''}
                                                </span>
                                            </span>
                                            <span className={`val ${signClass(row.value)}`}>{formatEuroSigned(row.value)}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}

                            {impact.notices.map((notice) => (
                                <p key={notice} className="participation-delta-notice">
                                    {NOTICE_TEXT[notice] || notice}
                                    {notice !== 'leeftijdsovergang' && (
                                        <button type="button" className="participation-delta-notice-link" onClick={() => setPersonaNavigation('data')}>
                                            Naar Gegevens
                                        </button>
                                    )}
                                </p>
                            ))}
                        </>
                    ) : (
                        <>
                            <p className="participation-delta-subheading">Per onderdeel</p>
                            {componentRows.map((row) => (
                                <div key={row.label} className="participation-delta-row">
                                    <span className="lbl">{row.label}</span>
                                    <span className={`val ${signClass(row.value)}`}>{formatEuroSigned(row.value)}</span>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                <div className="participation-moments">
                    {/* Beginsituatie */}
                    <div className={`participation-moment-column${beginLocked ? ' participation-moment-locked' : ''}`}>
                        <div className="participation-moment-header">
                            <div>
                                <h3>Beginsituatie</h3>
                                <span className="participation-moment-subtitle">
                                    {beginLocked ? 'Eenmalig vastgelegd · Vergrendeld voor bewerking' : 'Eenmalig vastleggen'}
                                </span>
                            </div>
                            {beginLocked && (
                                <span className="participation-pill participation-pill-locked">
                                    <LockSvg /> Vergrendeld
                                </span>
                            )}
                        </div>

                        {beginLocked
                            ? <ParticipationLockedSummary profile={profileT0} parameters={parameters} />
                            : <ParticipationProfileForm item={item} moment="t0" parameters={parameters} />}

                        <hr className="participation-sep" />

                        <ParticipationResult state={impact.t0} model={impact.model} muted label="Berekend resultaat" />

                        <div className="participation-card-actions">
                            {beginLocked ? (
                                <button type="button" className="participation-btn participation-btn-ghost" onClick={() => setBeginLocked(false)}>
                                    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 2.5l2 2-8 8H3.5v-2l8-8z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>
                                    Ontgrendelen om te bewerken
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="participation-btn participation-btn-primary"
                                    onClick={() => setBeginLocked(true)}
                                    disabled={!profileT0.mainActivity}
                                >
                                    Vastleggen
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Huidige situatie */}
                    <div className="participation-moment-column">
                        <div className="participation-moment-header">
                            <div>
                                <h3>Huidige situatie</h3>
                                <span className="participation-moment-subtitle">Werk je bij elke meting bij</span>
                            </div>
                            <span className="participation-pill participation-pill-live">
                                <span className="participation-dot" /> Actief
                            </span>
                        </div>

                        <ParticipationProfileForm item={item} moment="t1" parameters={parameters} />

                        <hr className="participation-sep" />

                        <ParticipationResult
                            state={impact.t1}
                            model={impact.model}
                            label="Berekend resultaat"
                            meta={currentUpdatedAt ? `Laatst bijgewerkt · ${timestampToUI(currentUpdatedAt)}` : undefined}
                        />

                        <div className="participation-card-actions">
                            <label className="participation-date-field">
                                <span>Meetdatum</span>
                                <input
                                    type="date"
                                    value={measurementDate}
                                    max={dagVan(new Date()) || ''}
                                    onChange={(e) => setMeasurementDate(e.target.value)}
                                    aria-label="Meetdatum van deze meting"
                                />
                            </label>
                            <button
                                type="button"
                                className="participation-btn participation-btn-primary"
                                onClick={recordMeasurement}
                                disabled={!profileT1.mainActivity || !measurementDate}
                            >
                                Meting vastleggen
                            </button>
                        </div>
                    </div>
                </div>

                {/* Vastgelegde metingen — datum bewerkbaar (boekjaar-toerekening) */}
                {sortedMeasurements.length > 0 && (
                    <div className="participation-measurements">
                        <div className="participation-measurements-header">
                            <h3>Vastgelegde metingen</h3>
                            <span className="participation-moment-subtitle">
                                De datum bepaalt in welk boekjaar de meting meetelt. Pas hem aan
                                wanneer een meting later is geregistreerd dan gemeten. Eén meting per dag.
                            </span>
                        </div>
                        {sortedMeasurements.map((m) => (
                            <div key={m.docid} className="participation-measurement-row">
                                <input
                                    type="date"
                                    defaultValue={dagVan(m.Timestamp) || ''}
                                    onBlur={measurementDateHandler(m)}
                                    aria-label="Datum van de meting"
                                />
                                <span className="lbl">{ladderLabel(m.LadderStep)}</span>
                                <span className={`val ${signClass(measurementValue(m))}`}>{formatEuroSigned(measurementValue(m))}</span>
                                <span className="participation-measurement-source">
                                    {m.Source === 'manual' ? 'Handmatig' : 'Automatisch'}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default Participatieladder
